import { Injectable, OnDestroy, computed, effect, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { Store } from '@ngrx/store';
import { Subscription, Subject, filter, map } from 'rxjs';

import { LocationRouteCffData } from '@fcom/core-api';
import { GlobalBookingTravelClass, TripType } from '@fcom/core/interfaces';
import { finShare } from '@fcom/rx';
import { unsubscribe, isNotBlank, isPresent, LocationRouteCffService, isEmptyObject } from '@fcom/core';
import {
  CommonFeatureState,
  GlobalBookingActions,
  selectGlobalBookingTripType,
  selectGlobalBookingTravelClass,
  selectGlobalBookingAvailableTravelClasses,
  selectGlobalBookingPaxAmount,
  TravelClassAvailabilityMap,
} from '@fcom/common/store';
import { LanguageService } from '@fcom/ui-translate';
import { profileOrUndefinedIfNotLoggedIn } from '@fcom/core/selectors';
import { mapCffsToTravelClasses } from '@fcom/common/utils/booking.utils';

import { BookingWidgetActions, BookingWidgetAppState, selectActiveTab } from '../store';
import { WidgetTab, AmountUpdateType, PaxUpdateEvent, NotificationWarning } from '../interfaces';
import { AVAILABLE_TRIP_TYPES } from '../constants';
import { BookingWidgetGtmService } from './booking-widget-gtm.service';
import { BookingWidgetFlightService } from './booking-widget-flight.service';
import { getGtmPassengers } from '../utils/utils.gtm';

@Injectable({ providedIn: 'root' })
export class BookingWidgetTripService implements OnDestroy {
  private store$ = inject<Store<CommonFeatureState & BookingWidgetAppState>>(Store);
  private languageService = inject(LanguageService);
  private bookingWidgetGtmService = inject(BookingWidgetGtmService);
  private locationRouteCffService = inject(LocationRouteCffService);
  private bookingWidgetFlightService = inject(BookingWidgetFlightService);

  private readonly _rawActiveTab = this.store$.selectSignal(selectActiveTab);
  readonly activeTab = computed(() => this._rawActiveTab() ?? WidgetTab.FLIGHT);

  private readonly _rawSelectedTripType = this.store$.selectSignal(selectGlobalBookingTripType);
  readonly selectedTripType = computed(() => this._rawSelectedTripType() ?? TripType.RETURN);

  readonly availableTripTypes = computed(() =>
    this.activeTab() === WidgetTab.AWARD
      ? AVAILABLE_TRIP_TYPES.filter((t) => t !== TripType.MULTICITY)
      : AVAILABLE_TRIP_TYPES
  );

  readonly selectedTravelClass = this.store$.selectSignal(selectGlobalBookingTravelClass);
  readonly availableTravelClasses = this.store$.selectSignal(selectGlobalBookingAvailableTravelClasses);
  readonly paxAmount = this.store$.selectSignal(selectGlobalBookingPaxAmount);

  private readonly _routeCffReady = signal(false);
  readonly routeCffReady = this._routeCffReady.asReadonly();

  private readonly _profile = toSignal(this.store$.pipe(profileOrUndefinedIfNotLoggedIn(), finShare()));

  private readonly _validLocations = computed(
    () =>
      this.bookingWidgetFlightService
        .bookingLocations()
        .filter(({ origin, destination }) => isNotBlank(origin?.locationCode) && isNotBlank(destination?.locationCode)),
    {
      equal: (prev, next) =>
        prev.length === next.length &&
        next.every(
          (l, i) =>
            l.origin?.locationCode === prev[i]?.origin?.locationCode &&
            l.destination?.locationCode === prev[i]?.destination?.locationCode
        ),
    }
  );

  private subscription = new Subscription();
  private setPaxDetailsGtm$: Subject<boolean> = new Subject<boolean>();

  constructor() {
    effect(() => {
      if (this.activeTab() === WidgetTab.AWARD && this.selectedTripType() === TripType.MULTICITY) {
        this.setTripType(TripType.RETURN);
      }
    });

    effect((onCleanup) => {
      const locations = this._validLocations();
      const tab = this.activeTab();
      const profile = this._profile();

      if (
        locations.length === 0 ||
        !(tab === WidgetTab.FLIGHT || (tab === WidgetTab.AWARD && isPresent(profile))) ||
        !locations.every((location) => location.origin && location.destination)
      ) {
        return;
      }

      this._routeCffReady.set(false);

      const subscription = this.locationRouteCffService
        .routeCffsFor(
          locations[0]?.origin.locationCode,
          locations[0]?.destination.locationCode,
          tab === WidgetTab.AWARD ? 'true' : 'false',
          this.languageService.localeValue,
          profile?.isCorporate ? 'true' : 'false'
        )
        .pipe(map((routeCffDataForFlights) => ({ routeCffDataForFlights, tab })))
        .subscribe(({ routeCffDataForFlights, tab: currentTab }) => {
          if (isEmptyObject(routeCffDataForFlights)) {
            this.store$.dispatch(
              BookingWidgetActions.setNotificationWarning({
                key:
                  currentTab === WidgetTab.AWARD
                    ? NotificationWarning.NO_AWARD_FLIGHTS_FOR_ROUTE
                    : NotificationWarning.NO_FLIGHTS_FOR_ROUTE,
                isActive: true,
              })
            );
            return;
          }
          this._routeCffReady.set(true);
          this.store$.dispatch(
            GlobalBookingActions.setAvailableClasses({
              availability: this.mapCffsToAvailableTravelClasses(routeCffDataForFlights),
            })
          );
        });

      onCleanup(() => subscription.unsubscribe());
    });

    this.setPaxDetailsGtm();
  }

  ngOnDestroy(): void {
    unsubscribe(this.subscription);
  }

  setTravelClass(travelClass: GlobalBookingTravelClass): void {
    this.bookingWidgetGtmService.trackElementEvent('travel-class', travelClass);
    this.store$.dispatch(GlobalBookingActions.setTravelClass({ travelClass }));
  }

  setActiveTab(tab: WidgetTab): void {
    this.store$.dispatch(BookingWidgetActions.setActiveTab({ activeTab: tab }));
  }

  setTripType(tripType: TripType): void {
    if (!tripType) {
      return;
    }

    this.bookingWidgetGtmService.trackElementEvent('travel-type', tripType.toUpperCase());
    this.store$.dispatch(GlobalBookingActions.setTripType({ tripType }));

    if (tripType === TripType.MULTICITY) {
      // we need to reset discount code when going to multicity tab because its not in use
      this.store$.dispatch(GlobalBookingActions.setDiscountCode(null));
    }
  }

  setReturnTripType(): void {
    this.setTripType(TripType.RETURN);
    this.bookingWidgetGtmService.trackElementEvent('add-return-button');
  }

  setPaxAmount({ paxType, amount, updateType }: PaxUpdateEvent): void {
    if (updateType === AmountUpdateType.DECREMENT) {
      this.store$.dispatch(GlobalBookingActions.decreasePaxAmountField({ field: paxType, decrement: amount }));
    } else {
      this.store$.dispatch(GlobalBookingActions.increasePaxAmountField({ field: paxType, increment: amount }));
    }
    this.setPaxDetailsGtm$.next(true);
  }

  private mapCffsToAvailableTravelClasses(locationRouteCffData: LocationRouteCffData): TravelClassAvailabilityMap {
    return mapCffsToTravelClasses(locationRouteCffData)
      .filter((travelClass) => travelClass !== GlobalBookingTravelClass.FIRST)
      .reduce((uniqueClasses, classes) => {
        uniqueClasses[classes] = true;
        return uniqueClasses;
      }, {}) as TravelClassAvailabilityMap;
  }

  private setPaxDetailsGtm(): void {
    this.subscription.add(
      this.setPaxDetailsGtm$.pipe(filter(Boolean)).subscribe(() => {
        const paxDetails = this.paxAmount();
        const { adults, children, infants } = getGtmPassengers(paxDetails);
        this.bookingWidgetGtmService.trackElementEvent(
          'pax-amount',
          `MAINPAX: ${adults}, CHD: ${children}, INF: ${infants}, MAINPAXTYPE: ADT`
        );
        this.setPaxDetailsGtm$.next(false);
      })
    );
  }
}
